import React, { useState, useEffect } from "react";
import {
  getProvider,
  updateProviderRequest,
  getOrganisation,
  getMerchantDailyTransactions,
} from "../../../config/LiveApi";
import { CiEdit } from "react-icons/ci";
import Modal from "./Modal";
import { toast } from "react-toastify";
import { ShimmerThumbnail } from "react-shimmer-effects";
import { useSelector } from "react-redux";
import { FaFilter } from "react-icons/fa";
import ProviderModal from "./ProviderModal";
import Spinner from "../../Spin/Spinner";
import DateFilter from "../../../components/common/DateFilter";
import DataTable from "../../../components/common/DataTable/DataTable";

const GetProvider = () => {
  const FIXED_DATE = "2025-01-01";
  const [providerTableData, setProviderTableData] = useState([]);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [orgOptions, setOrgOptions] = useState([]);
  const [selectedOrgName, setSelectedOrgName] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedProviderName, setSelectedProviderName] = useState("");
  const [transactionData, setTransactionData] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());
  const [activeFilter, setActiveFilter] = useState("");

  let token = useSelector((store) => store.userReducer.token);
  const role = useSelector((store) => store.userReducer.role);

  const handleDateChange = (startDate, endDate) => {
    setSelectedStartDate(new Date(startDate));
    setSelectedEndDate(new Date(endDate));
  };

  const handleProviderClick = async (providerId, name) => {
    setSelectedProviderId(providerId);
    setSelectedProviderName(name);
    setTransactionData([]); // Clear previous data
    setShowModal(true); // Show modal immediately
    setIsLoading(true);

    const startDate = selectedStartDate;
    const endDate = selectedEndDate;

    try {
      const data = await getMerchantDailyTransactions(
        providerId,
        startDate.toISOString(),
        endDate.toISOString(),
      );
      setTransactionData(data);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching transaction data", error);
    } finally {
      setIsLoading(false); // Hide spinner after fetching
    }
  };

  useEffect(() => {
    const fetchOrganisations = async () => {
      try {
        const response = await getOrganisation(token);
        const orgData = response.data;
        setOrgOptions(orgData);
      } catch (err) {
        console.error("Error fetching organisations:", err.message);
        toast.error("Unable to fetch organisations.");
      }
    };

    fetchOrganisations();
  }, [token]);

  const fetchProviders = async () => {
    setIsLoading(true);
    const response = await getProvider(token);
    setProviderTableData(response.data.providers || []);
    setIsLoading(false);
  };

  const updateProvider = (item) => {
    let min = "";
    let max = "";

    if (item.validLength && item.validLength.includes("-")) {
      const parts = item.validLength.split("-");
      min = parts[0] || "";
      max = parts[1] || "";
    }

    setCurrentProvider({
      ...item,
      minLength: min,
      maxLength: max,
    });

    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setCurrentProvider(null);
  };

  const handleUpdateSubmit = async (event) => {
    event.preventDefault();

    if (!currentProvider) return;

    const type = currentProvider.type;

    // Payin limit check
    if (type === "payin" || type === "both") {
      const min = Number(currentProvider.payinMinPerTxn);
      const max = Number(currentProvider.payinMaxPerTxn);
      if (min && max && max <= min) {
        toast.error("Maximum Payin must be greater than Minimum Payin.");
        return;
      }
    }

    // Payout limit check
    if (type === "payout" || type === "both") {
      const min = Number(currentProvider.payoutMinPerTxn);
      const max = Number(currentProvider.payoutMaxPerTxn);

      if (min && max && max <= min) {
        toast.error("Maximum Payout must be greater than Minimum Payout.");
        return;
      }
    }

    // Time validation
    // if (currentProvider.startTime && currentProvider.endTime) {
    //   // If startTime / endTime come as ISO strings, extract time
    //   const start = currentProvider.startTime.includes("T")
    //     ? currentProvider.startTime.split("T")[1].slice(0, 5)
    //     : currentProvider.startTime;

    //   const end = currentProvider.endTime.includes("T")
    //     ? currentProvider.endTime.split("T")[1].slice(0, 5)
    //     : currentProvider.endTime;

    //   const [sh, sm] = start.split(":").map(Number);
    //   const [eh, em] = end.split(":").map(Number);

    //   const startMinutes = sh * 60 + sm;
    //   const endMinutes = eh * 60 + em;

    //   if (endMinutes <= startMinutes) {
    //     toast.error("End time must be greater than Start time.");
    //     return;
    //   }
    // }

    try {
      await updateProviderRequest(token, currentProvider.id, currentProvider);

      setProviderTableData((prevProviderData) =>
        prevProviderData.map((provider) =>
          provider.id === currentProvider.id
            ? { ...provider, ...currentProvider }
            : provider,
        ),
      );

      toast.success("User updated successfully.");
      handleModalClose();
    } catch (err) {
      console.error(err.message);
      toast.error("Failed to update provider.");
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let parsedValue = value;

    if (name === "isActive") {
      parsedValue = JSON.parse(value); // Convert "true"/"false" string to boolean
    } else if (["runningWalletAmount", "dailyPayinAmount"].includes(name)) {
      parsedValue = parseFloat(value) || 0; // Convert only these fields to float
    } else if (["minLength", "maxLength"].includes(name)) {
      parsedValue = value === "" ? "" : parseInt(value, 10);

      // handle min/max + auto-generate validLength
      return setCurrentProvider((prev) => {
        const updated = { ...prev, [name]: parsedValue };

        // both min & max present → create "Min-Max"
        if (updated.minLength !== "" && updated.maxLength !== "") {
          updated.validLength = `${updated.minLength}-${updated.maxLength}`;
        } else {
          updated.validLength = "";
        }

        return updated;
      });
    } else if (["startTime", "endTime"].includes(name)) {
      parsedValue = `${FIXED_DATE}T${value}`;
    }

    setCurrentProvider((prev) => ({ ...prev, [name]: parsedValue }));
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  const columns = [
    {
      key: "serial",
      header: "S.No",
      render: (_, index) => index + 1,
    },

    {
      key: "providerName",
      header: "Provider Name",
      wrapHeader: true,
      render: (row) => (
        <p
          className="user-table-name"
          style={{ width: "10rem" }}
          onClick={() => handleProviderClick(row.id, row.providerName)}
        >
          {row.providerName}
        </p>
      ),
    },

    {
      key: "isActive",
      header: "Active",
      filterable: true,
      filterType: "menu",
      filterValue: activeFilter,
      onFilterChange: setActiveFilter,
      filterOptions: [
        { label: "All", value: "" },
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
      ],
      render: (row) => (
        <span
          className={`user-table-head ${
            row.isActive ? "status-yes" : "status-no"
          }`}
        >
          {row.isActive ? "Yes" : "No"}
        </span>
      ),
    },

    ...(role === "super" || role === "operation"
      ? [
          {
            key: "orgName",
            header: "Org Name",
            wrapHeader: true,
            filterable: true,
            filterType: "select",
            filterValue: selectedOrgName || "All",
            onFilterChange: setSelectedOrgName,
            filterOptions: [
              { label: "All", value: "All" },
              ...orgOptions.map((org) => ({
                label: org.orgName,
                value: org.orgName,
              })),
            ],
          },
        ]
      : []),

    {
      key: "type",
      header: "Type",
      filterable: true,
      filterType: "menu",
      filterValue: typeFilter,
      onFilterChange: setTypeFilter,
      filterOptions: [
        { label: "All", value: "" },
        { label: "Payin", value: "payin" },
        { label: "Payout", value: "payout" },
        { label: "Both", value: "both" },
      ],
    },

    {
      key: "providerPayinComm",
      header: "Payin Commission",
      render: (row) => Number(row.providerPayinComm || 0).toFixed(3),
    },

    {
      key: "providerPayoutComm",
      header: "Payout Commission",
      render: (row) => Number(row.providerPayoutComm || 0).toFixed(3),
    },

    {
      key: "payinMinPerTxn",
      header: "Minimum Payin per Transaction",
      wrapHeader: true,
      render: (row) => Number(row.payinMinPerTxn || 0).toFixed(2),
    },

    {
      key: "payinMaxPerTxn",
      header: "Maximum Payin per Transaction",
      wrapHeader: true,
      render: (row) => Number(row.payinMaxPerTxn || 0).toFixed(2),
    },

    {
      key: "payoutMinPerTxn",
      header: "Minimum Payout per Transaction",
      wrapHeader: true,
      render: (row) => Number(row.payoutMinPerTxn || 0).toFixed(2),
    },

    {
      key: "payoutMaxPerTxn",
      header: "Maximum Payout per Transaction",
      wrapHeader: true,
      render: (row) => Number(row.payoutMaxPerTxn || 0).toFixed(2),
    },

    {
      key: "totalPayinLimit",
      header: "Total Payin",
      render: (row) => Number(row.totalPayinLimit || 0).toFixed(2),
    },

    {
      key: "totalPayoutLimit",
      header: "Total Payout",
      render: (row) => Number(row.totalPayoutLimit || 0).toFixed(2),
    },

    {
      key: "currentPayinAmount",
      header: "Current Payin Amount",
      wrapHeader: true,
      render: (row) => Number(row.currentPayinAmount || 0).toFixed(2),
    },

    {
      key: "currentPayoutAmount",
      header: "Current Payout Amount",
      wrapHeader: true,
      render: (row) => Number(row.currentPayoutAmount || 0).toFixed(2),
    },

    {
      key: "runningWalletAmount",
      header: "Running Wallet Balance",
      wrapHeader: true,
      render: (row) => Number(row.runningWalletAmount || 0).toFixed(2),
    },

    {
      key: "dailyPayinAmount",
      header: "Daily Payin Amount",
      wrapHeader: true,
      render: (row) => Number(row.dailyPayinAmount || 0).toFixed(2),
    },

    {
      key: "startTime",
      header: "Start Time",
      render: (row) =>
        new Date(row.startTime).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
    },

    {
      key: "endTime",
      header: "End Time",
      render: (row) =>
        new Date(row.endTime).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: true,
        }),
    },

    {
      key: "update",
      header: "Update",
      render: (row) => (
        <CiEdit
          style={{
            width: "1.2rem",
            height: "1.2rem",
            cursor: "pointer",
          }}
          onClick={() => updateProvider(row)}
        />
      ),
    },
  ];

  const filteredData = providerTableData
    .filter((item) => {
      if (!selectedOrgName || selectedOrgName === "All") {
        return true;
      }
      return item.orgName === selectedOrgName;
    })
    .filter((item) => !typeFilter || item.type === typeFilter)
    .filter((item) => {
      if (!activeFilter) return true;
      if (activeFilter === "yes") return item.isActive === true;
      if (activeFilter === "no") return item.isActive === false;
      return true;
    })
    .sort((a, b) => (a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1));

  return (
    <>
      <div>
        <div className="dash-head">
          <h1 style={{ marginBottom: "20px" }}>List of Providers</h1>
        </div>
        <DateFilter onDateChange={handleDateChange} />

        {isLoading ? (
          <ShimmerThumbnail height={600} rounded />
        ) : (
          <div className="table-main-div">
            <DataTable columns={columns} data={filteredData} />
          </div>
        )}
        {isModalOpen && (
          <Modal
            onClose={handleModalClose}
            onSubmit={handleUpdateSubmit}
            formData={currentProvider}
            handleInputChange={handleInputChange}
          />
        )}
        {showModal &&
          (isLoading ? (
            <Spinner style />
          ) : (
            <ProviderModal
              transactionData={transactionData}
              setShowModal={setShowModal}
              startDate={selectedStartDate.toISOString()}
              endDate={selectedEndDate.toISOString()}
              userName={selectedProviderName}
            />
          ))}
      </div>
    </>
  );
};

export default GetProvider;
